/**
 * SEO Utility Functions
 * Helper functions for generating SEO-friendly content
 */

/**
 * Generate SEO-friendly title
 */
export const generateTitle = (pageTitle, siteName = 'MahStry') => {
  if (!pageTitle) return `${siteName} - India's #1 Online Study Platform`;
  return `${pageTitle} | ${siteName}`;
};

/**
 * Generate SEO-friendly description
 */
export const generateDescription = (content, maxLength = 160) => {
  if (!content) {
    return 'MahStry is India\'s leading online study platform for competitive exams. Practice mock tests, join study rooms, access courses, check results & admit cards.';
  }
  const cleaned = content.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  if (cleaned.length <= maxLength) return cleaned;
  return cleaned.substring(0, maxLength - 3) + '...';
};

/**
 * Generate keywords from content
 */
export const generateKeywords = (baseKeywords = [], additionalKeywords = []) => {
  const defaultKeywords = [
    'MahStry',
    'online study platform',
    'mock tests',
    'competitive exams',
    'exam preparation',
    'India',
  ];
  return [...new Set([...defaultKeywords, ...baseKeywords, ...additionalKeywords])].join(', ');
};

/**
 * Generate canonical URL
 */
export const generateCanonicalUrl = (path, baseUrl = 'https://mahstry.com') => {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${cleanPath}`;
};

/**
 * Generate structured data for exam/test pages
 */
export const generateExamStructuredData = (examData) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: examData.name || 'Competitive Exam Preparation',
    description: examData.description || 'Comprehensive preparation for competitive exams',
    provider: {
      '@type': 'Organization',
      name: 'MahStry',
      url: 'https://mahstry.com',
    },
    courseCode: examData.code,
    educationalLevel: 'Professional',
    teaches: examData.subjects || [],
    ...(examData.duration && { timeRequired: examData.duration }),
    ...(examData.image && { image: examData.image }),
  };
};

/**
 * Generate structured data for FAQ pages
 */
export const generateFAQStructuredData = (faqs) => {
  if (!faqs || faqs.length === 0) return null;
  
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
};

/**
 * Generate structured data for breadcrumbs
 */
export const generateBreadcrumbStructuredData = (items, baseUrl = 'https://mahstry.com') => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url.startsWith('http') ? item.url : `${baseUrl}${item.url}`,
    })),
  };
};

/**
 * Generate structured data for organization
 */
export const generateOrganizationStructuredData = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'EducationalOrganization',
    name: 'Mahstry Private Limited',
    alternateName: 'MahStry',
    url: 'https://mahstry.com',
    logo: 'https://mahstry.com/icon-512x512.png',
    description: 'India\'s leading online study platform for competitive exams',
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'IN',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Customer Service',
      email: 'support@mahstry.com',
      availableLanguage: ['English', 'Hindi'],
    },
    sameAs: [
      'https://twitter.com/mahstry',
      'https://facebook.com/mahstry',
      'https://linkedin.com/company/mahstry',
    ],
    taxID: '08AAUCM5530E1Z9',
  };
};

/**
 * Generate structured data for course/test
 */
export const generateCourseStructuredData = (courseData) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: courseData.name,
    description: courseData.description,
    provider: {
      '@type': 'Organization',
      name: 'MahStry',
    },
    ...(courseData.image && { image: courseData.image }),
    ...(courseData.duration && { timeRequired: courseData.duration }),
    ...(courseData.rating && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: courseData.rating,
        ratingCount: courseData.reviewCount || 0,
      },
    }),
  };
};

/**
 * Sanitize text for meta descriptions
 */
export const sanitizeForMeta = (text, maxLength = 160) => {
  if (!text) return '';
  return text
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/&[^;]+;/g, '') // Remove HTML entities
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim()
    .substring(0, maxLength)
    .replace(/\s+\S*$/, ''); // Don't cut words
};

/**
 * Extract keywords from text
 */
export const extractKeywords = (text, maxKeywords = 10) => {
  if (!text) return [];
  
  const commonWords = new Set([
    'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for',
    'of', 'with', 'by', 'from', 'as', 'is', 'was', 'are', 'were', 'been',
    'be', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'should',
    'could', 'may', 'might', 'must', 'can', 'this', 'that', 'these', 'those',
  ]);
  
  const words = text
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .split(/\s+/)
    .filter(word => word.length > 3 && !commonWords.has(word));
  
  const frequency = {};
  words.forEach(word => {
    frequency[word] = (frequency[word] || 0) + 1;
  });
  
  return Object.entries(frequency)
    .sort((a, b) => b[1] - a[1])
    .slice(0, maxKeywords)
    .map(([word]) => word);
};

/**
 * Generate meta description from content
 * Alias for generateDescription for backward compatibility
 */
export const generateMetaDescription = (content, maxLength = 160) => {
  return generateDescription(content, maxLength);
};

/**
 * Generate page structured data
 */
export const generatePageStructuredData = (pageType, data = {}) => {
  // Handle both 'url' and 'canonical' properties
  const url = data.url || data.canonical || "https://mahstry.com";
  
  const baseData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": data.title || "MahStry Study Platform",
    "description": data.description || "India's leading online study platform for competitive exams",
    "url": url,
    "isPartOf": {
      "@type": "WebSite",
      "name": "MahStry",
      "url": "https://mahstry.com"
    },
    "publisher": {
      "@type": "EducationalOrganization",
      "name": "Mahstry Private Limited",
      "alternateName": "MahStry",
      "url": "https://mahstry.com",
      "taxID": "08AAUCM5530E1Z9"
    }
  };

  switch (pageType) {
    case 'home':
      return {
        ...baseData,
        "@type": "WebSite",
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://mahstry.com/search?q={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      };

    case 'mock-test':
      return {
        ...baseData,
        "@type": "Quiz",
        "name": data.title || "Mock Test",
        "description": data.description || "Practice mock test for competitive exams",
        "educationalLevel": "Advanced",
        "learningResourceType": "Assessment",
        "about": {
          "@type": "Thing",
          "name": data.examType || "Competitive Exam"
        }
      };

    case 'course':
      return {
        ...baseData,
        "@type": "Course",
        "name": data.title || "Study Course",
        "description": data.description || "Comprehensive study course for exam preparation",
        "provider": {
          "@type": "EducationalOrganization",
          "name": "Mahstry Private Limited",
          "alternateName": "MahStry",
          "url": "https://mahstry.com",
          "taxID": "08AAUCM5530E1Z9"
        },
        "courseMode": "online",
        "educationalLevel": "Advanced"
      };

    case 'live-class':
      return {
        ...baseData,
        "@type": "Event",
        "name": data.title || "Study Room Session",
        "description": data.description || "Collaborative study session",
        "eventStatus": "EventScheduled",
        "eventAttendanceMode": "OnlineEventAttendanceMode",
        "location": {
          "@type": "VirtualLocation",
          "url": data.url || "https://mahstry.com/live-classes"
        }
      };

    case 'exam-result':
      return {
        ...baseData,
        "@type": "Article",
        "headline": data.title || "Exam Results",
        "description": data.description || "Check your exam results and performance",
        "articleSection": "Education",
        "about": {
          "@type": "Thing",
          "name": "Exam Results"
        }
      };

    case 'admit-card':
      return {
        ...baseData,
        "@type": "Article",
        "headline": data.title || "Admit Card",
        "description": data.description || "Download your exam admit card",
        "articleSection": "Education",
        "about": {
          "@type": "Thing",
          "name": "Admit Card"
        }
      };

    default:
      return baseData;
  }
};
